import React, { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from '../utils/logger';
import {
  Save,
  TestTube,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  ArrowLeft,
  RefreshCw,
  Users,
  ChevronDown,
  RefreshCcw,
  Zap,
  Thermometer,
  Wind,
  Clock,
  X,
  Activity,
  Upload,
  Image,
  User,
  LogOut,
  Languages,
  Trash2,
  Mail,
  Palette
} from 'lucide-react';
// Theme is fixed to dark mode - useTheme removed
import { useAppMode } from '../contexts/AppModeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ApiCredentials, PowerMeter } from '../types';
import { ultronTuringApi, NotificationPreferences } from '../services/ultronTuringApi';
import { mcpOAuthService } from '../services/mcpOAuthService';
import { APP_VERSION, BUILD_STRING, APP_NAME } from '../constants/version';
import { useNavigate } from 'react-router-dom';
import { getAllImportedDevicesWithBattery, getDeviceDisplayName } from '../utils/deviceUtils';
import {
  saveDashboardSettings,
  saveCompanyBranding,
  saveBatteryAlert,
  getBatteryAlert,
  saveLanguage as saveLanguageToFirestore,
  type DashboardSettings,
  type CompanyBranding,
  type BatteryAlertSettings
} from '../services/userPreferencesApi';
import { syncBatteryAlerts, getGlobalBatteryAlert } from '../services/batteryAlertService';
import { DeviceModalThemeEditor } from '../components/settings';

interface SettingsProps {
  onBack: () => void;
}

interface SelectedDevices {
  [deviceId: string]: boolean;
}

const Settings: React.FC<SettingsProps> = ({ onBack }) => {
  // Theme is fixed to dark mode
  const { mode } = useAppMode();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  // Form state
  const [credentials, setCredentials] = useState<ApiCredentials>({
    apiKey: '',
    appId: 'KLMGeDMy', // Default to Personal Type App ID
    groupId: '',
    baseUrl: 'https://turing.ultronsmart.com'
  });

  const [subscriptionType, setSubscriptionType] = useState<'personal' | 'enterprise'>('personal');

  // User info state
  const [userInfo, setUserInfo] = useState<{
    name?: string;
    email?: string;
    photo?: string;
  } | null>(null);
  const [isOAuthUser, setIsOAuthUser] = useState(false);

  const [settings, setSettings] = useState({
    powerMeterRefreshInterval: 30,  // Power Meter Cards refresh (seconds)
    powerChartRefreshInterval: 1800, // Power Consumption Chart refresh (seconds) - 30 minutes
    showOfflineDevices: true,
    autoRefresh: true,
    demoMode: false,  // Default to off
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Taipei',  // Auto-detect browser timezone
    keepLoggedIn: false  // Default: false - 保持登入（OAuth 自動刷新 token）
  });

  // UI state
  const [showApiKey, setShowApiKey] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [isSavingApiKey, setIsSavingApiKey] = useState(false);
  
  // Group and device state
  const [availableGroups, setAvailableGroups] = useState<Array<{id: string, name: string}>>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [isApiKeySaved, setIsApiKeySaved] = useState(false);
  const [groupDevices, setGroupDevices] = useState<PowerMeter[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState<SelectedDevices>({});
  const [selectAll, setSelectAll] = useState(true);
  
  // Logo upload state
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [logoError, setLogoError] = useState<string>('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isLogoHovered, setIsLogoHovered] = useState(false);

  // Favicon upload state
  const [faviconUrl, setFaviconUrl] = useState<string>('');
  const [faviconError, setFaviconError] = useState<string>('');
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const [isFaviconHovered, setIsFaviconHovered] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Company name state
  const [companyName, setCompanyName] = useState<string>('Your Company Name');
  const [displayCompanyName, setDisplayCompanyName] = useState<string>('Your Company Name');

  // Settings navigation state
  const [activeSection, setActiveSection] = useState<'company' | 'dashboard' | 'notification' | 'cache' | 'language' | 'themes'>('company');


  // Battery Alert state
  const [batteryAlertEnabled, setBatteryAlertEnabled] = useState<boolean>(false);
  const [batteryThreshold, setBatteryThreshold] = useState<number>(20);
  const [batteryNotificationChannels, setBatteryNotificationChannels] = useState<string[]>(['websocket', 'email']);
  const [useCustomEmail, setUseCustomEmail] = useState<boolean>(false);
  const [customEmail, setCustomEmail] = useState<string>('');
  const [isTestingBatteryAlert, setIsTestingBatteryAlert] = useState<boolean>(false);
  const [batteryDeviceCount, setBatteryDeviceCount] = useState<number>(0);
  const [showBatteryDevicesModal, setShowBatteryDevicesModal] = useState<boolean>(false);
  // Track saved state to restore on tab switch (don't persist unsaved toggle changes)
  const [savedBatteryAlertConfig, setSavedBatteryAlertConfig] = useState<BatteryAlertSettings | null>(null);
  // Track previous activeSection to detect tab entry vs config reload
  const prevActiveSectionRef = useRef<string | null>(null);
  // Track last save timestamp to prevent stale async loads from overwriting recent saves
  const lastSaveTimeRef = useRef<number>(0);
  const [batteryDevicesWithLevel, setBatteryDevicesWithLevel] = useState<Array<{
    sn: string;
    name: string;
    batteryLevel?: number;
  }>>([]);

  // System Notification Preferences state
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>({
    alert: ['websocket', 'email'],
    automation: ['websocket', 'email'],
    battery: ['websocket', 'email']
  });
  const [savedNotificationPreferences, setSavedNotificationPreferences] = useState<NotificationPreferences | null>(null);
  const [isLoadingNotificationPrefs, setIsLoadingNotificationPrefs] = useState<boolean>(false);
  const [isSavingNotificationPrefs, setIsSavingNotificationPrefs] = useState<boolean>(false);
  const [notificationCustomEmails, setNotificationCustomEmails] = useState<{
    alert?: string;
    automation?: string;
    battery?: string;
  }>({});
  const [savedNotificationCustomEmails, setSavedNotificationCustomEmails] = useState<{
    alert?: string;
    automation?: string;
    battery?: string;
  }>({});
  const [useNotificationCustomEmail, setUseNotificationCustomEmail] = useState<{
    alert?: boolean;
    automation?: boolean;
    battery?: boolean;
  }>({});
  const [savedUseNotificationCustomEmail, setSavedUseNotificationCustomEmail] = useState<{
    alert?: boolean;
    automation?: boolean;
    battery?: boolean;
  }>({});

  // Get the correct storage key (legacy mode-specific keys removed)
  const getSelectedDevicesKey = () => {
    return 'ultron-selected-devices';
  };

  // Filter devices - IntelliSpace mode shows all devices (legacy mode filters removed)
  const filterDevicesByMode = (devices: PowerMeter[]) => {
    // IntelliSpace mode - return all devices
    return devices;
  };

  // Function to load groups from API
  const loadGroupsFromAPI = async () => {
    try {
      // Use Ultron Turing API to get locations
      const locations: any = await ultronTuringApi.queryLocations();
      logger.log('🔧 Settings: Locations from Turing:', locations);

      // Handle empty locations array - NO DEFAULT GROUP
      const locationsData: any = locations?.data || locations || {};
      const hasLocations = locations && (
        (locationsData?.locations && locationsData.locations.length > 0) ||
        (locationsData?.groups && locationsData.groups.length > 0) ||
        (Array.isArray(locationsData) && locationsData.length > 0)
      );
      if (!hasLocations) {
        logger.log('🔧 No locations found');
        setAvailableGroups([]);
        return [];
      }

      // Extract locations array from response
      const locationsArray = locationsData?.locations || locationsData?.groups || (Array.isArray(locationsData) ? locationsData : []);

      // Transform locations to groups format
      const groups = locationsArray.map((loc: any) => ({
        id: loc.id || loc.groupId || '',
        name: loc.name || 'Unknown Location',
        deviceCount: loc.devices?.length || 0
      }));

      setAvailableGroups(groups);
      return groups;
    } catch (error) {
      logger.error('Error loading groups:', error);
      // NO DEFAULT GROUP - let user handle it
      setAvailableGroups([]);
      return [];
    }
  };

  // Load current settings on mount
  useEffect(() => {
    // Check if user is using OAuth
    const authMode = sessionStorage.getItem('auth_mode');
    if (authMode === 'oauth') {
      setIsOAuthUser(true);
      // Fetch user info from GetUserInfo API (if available)
      const fetchUserInfo = async () => {
        try {
          // Check if we have a valid token first
          const tokens = sessionStorage.getItem('ultron_tokens') || localStorage.getItem('ultron_tokens');
          if (!tokens) {
            logger.log('⚠️ Settings: No tokens available, skipping GetUserInfo');
            return;
          }

          const response = await ultronTuringApi.getUserInfo();
          
          // Handle case where endpoint is not available (404)
          if (response?.available === false) {
            logger.log('⚠️ Settings: GetUserInfo endpoint not available, using fallback');
            // Try to get user info from token
            try {
              const tokenData = JSON.parse(tokens);
              if (tokenData.email) {
                setUserInfo({ email: tokenData.email });
              }
            } catch {
              // Ignore parse errors
            }
            return;
          }
          
          if (response?.userInfo) {
            setUserInfo({
              name: response.userInfo.name,
              email: response.userInfo.email,
              photo: response.userInfo.photoURL
            });
          }
        } catch (error) {
          logger.warn('⚠️ Settings: Failed to fetch user info (non-critical):', error);
          // Don't show error - this is optional functionality
        }
      };
      fetchUserInfo();
    } else {
      setIsOAuthUser(false);
    }

    // First try to load from localStorage
    const storedCredentials = localStorage.getItem('ultron-energy-credentials');
    if (storedCredentials) {
      try {
        const parsedCredentials = JSON.parse(storedCredentials);
        setCredentials(parsedCredentials);
        // Set subscription type based on stored App ID
        if (parsedCredentials.appId === 'gP4hKtHG') {
          setSubscriptionType('enterprise');
        } else {
          setSubscriptionType('personal');
        }
        // Set selected group from stored credentials
        if (parsedCredentials.groupId) {
          setSelectedGroupId(parsedCredentials.groupId);
        }
        // Credentials are now handled by environment variables

        // If we have API key, mark it as saved and load groups
        if (parsedCredentials.apiKey) {
          setIsApiKeySaved(true);
          // Load groups from API
          loadGroupsFromAPI().then(groups => {
            if (parsedCredentials.groupId && groups.length > 0) {
              // Make sure the stored group is in the list
              const groupExists = groups.some((g: any) => g.id === parsedCredentials.groupId);
              if (!groupExists && groups.length > 0) {
                // If stored group doesn't exist, select first
                setSelectedGroupId(groups[0].id);
                setCredentials(prev => ({ ...prev, groupId: groups[0].id }));
              }
            }
          });
        }
      } catch (error) {
        logger.error('Error parsing stored credentials:', error);
      }
    }
    
    // Load selected devices based on mode
    const storageKey = getSelectedDevicesKey();
    const storedSelectedDevices = localStorage.getItem(storageKey);
    if (storedSelectedDevices) {
      try {
        const parsed = JSON.parse(storedSelectedDevices);
        setSelectedDevices(parsed);
        // Don't check selectAll here, wait for devices to load
      } catch (error) {
        logger.error('Error parsing selected devices:', error);
      }
    }

    // Load dashboard settings (priority: Firestore > localStorage > defaults)
    const loadDashboardSettings = async () => {
      try {
        // Try to load from Firestore first (for OAuth users)
        if (authMode === 'oauth') {
          const { getUserPreferences } = await import('../services/userPreferencesApi');
          const preferences = await getUserPreferences();

          if (preferences.dashboardSettings) {
            setSettings(prev => ({
              ...prev,
              ...preferences.dashboardSettings,
              // Ensure keepLoggedIn is loaded with fallback to false
              keepLoggedIn: preferences.dashboardSettings?.keepLoggedIn ?? false
            }));
            logger.log('✅ Dashboard settings loaded from Firestore');
            return;
          }
        }
      } catch (error) {
        logger.warn('Failed to load from Firestore, falling back to localStorage:', error);
      }

      // Fallback to localStorage
      const storedSettings = localStorage.getItem('ultron-energy-settings');
      if (storedSettings) {
        try {
          const parsedSettings = JSON.parse(storedSettings);
          setSettings(prev => ({ ...prev, ...parsedSettings }));
          logger.log('✅ Dashboard settings loaded from localStorage');
        } catch (error) {
          logger.error('Error parsing stored settings:', error);
        }
      } else {
        // First time - save default settings
        const defaultSettings = {
          powerMeterRefreshInterval: 30,
          powerChartRefreshInterval: 1800,
          showOfflineDevices: true,
          autoRefresh: true,
          demoMode: false,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Taipei',
          keepLoggedIn: false  // Default: false
        };
        localStorage.setItem('ultron-energy-settings', JSON.stringify(defaultSettings));
        setSettings(defaultSettings);
        logger.log('✅ Dashboard settings initialized with defaults');
      }
    };

    loadDashboardSettings();
    
    // Load saved logo
    const savedLogo = localStorage.getItem('ultron-company-logo');
    if (savedLogo) {
      setLogoUrl(savedLogo);
    }

    // Load saved favicon
    const savedFavicon = localStorage.getItem('ultron-company-favicon');
    if (savedFavicon) {
      setFaviconUrl(savedFavicon);
      // Apply favicon to document
      updateFaviconInDocument(savedFavicon);
    }

    // Load saved company name
    const savedCompanyName = localStorage.getItem('ultron-company-name');
    if (savedCompanyName) {
      setCompanyName(savedCompanyName);
      setDisplayCompanyName(savedCompanyName);
    }

    // Load Battery Alert settings
    // Source of truth: Turing Server (for enabled/threshold) + Firestore (for notification prefs)
    const loadBatteryAlertSettings = async () => {
      // Capture load start time to detect if a save happens during the async load
      const loadStartTime = Date.now();
      
      const authMode = sessionStorage.getItem('auth_mode');
      
      // Step 1: Check Turing Server for existing global battery alert
      // Read the alert's `enabled` field - alert can exist but be disabled
      let serverAlertEnabled = false;
      let serverThreshold = 20;
      
      try {
        logger.log('🔋 [Settings] Checking Turing server for battery alert...');
        const serverAlert = await getGlobalBatteryAlert();
        if (serverAlert) {
          // Check the alert's enabled field - it could be true or false!
          serverAlertEnabled = serverAlert.enabled === true;
          serverThreshold = serverAlert.threshold || 20;
          logger.log('🔋 [Settings] Found battery alert on server:', {
            id: serverAlert.id,
            enabled: serverAlert.enabled,  // Log the actual value
            threshold: serverThreshold
          });
        } else {
          logger.log('🔋 [Settings] No battery alert found on server');
          serverAlertEnabled = false;
        }
      } catch (error) {
        logger.error('🔋 [Settings] Failed to check Turing server:', error);
        // On error, we can't determine server state - show as disabled
        serverAlertEnabled = false;
      }
      
      // Step 2: Load notification preferences from Firestore (for OAuth users)
      // These are user-specific settings not stored in the alert rule
      let notificationChannels: string[] = ['websocket', 'email'];
      let useCustomEmail = false;
      let customEmail = '';
      
      if (authMode === 'oauth') {
        try {
          const userPrefs = await getBatteryAlert();
          if (userPrefs) {
            logger.log('🔋 [Settings] Loaded notification prefs from Firestore');
            notificationChannels = userPrefs.notificationChannels?.length 
              ? userPrefs.notificationChannels 
              : ['websocket', 'email'];
            useCustomEmail = userPrefs.useCustomEmail || false;
            customEmail = userPrefs.customEmail || '';
          }
        } catch (error) {
          logger.warn('🔋 [Settings] Failed to load prefs from Firestore, using defaults');
        }
      }
      
      // Check if a save happened during the async load - if so, skip applying stale data
      if (lastSaveTimeRef.current > loadStartTime) {
        logger.log('🔋 Skipping stale async load - a save happened after load started');
        return;
      }
      
      // Build config from server + user preferences
      const batteryAlertConfig: BatteryAlertSettings = {
        enabled: serverAlertEnabled,
        threshold: serverThreshold,
        notificationChannels,
        useCustomEmail,
        customEmail
      };
      
      // Only set savedBatteryAlertConfig here - don't directly update form state!
      // The restoration effect will handle applying to form if user hasn't edited
      // This prevents async loads from overwriting user's in-progress edits
      setSavedBatteryAlertConfig(batteryAlertConfig);
      
      logger.log('🔋 [Settings] Loaded config (restoration effect will apply if no user edits):', batteryAlertConfig);
    };
    loadBatteryAlertSettings();

    // Load battery device count from QueryStatus API (直接用 system.battery 判斷)
    const loadBatteryDeviceCount = async () => {
      try {
        const rawStatusData = await ultronTuringApi.queryStatus([], { detailed: false });
        if (rawStatusData?.data && Array.isArray(rawStatusData.data)) {
          const count = rawStatusData.data.filter((item: any) => 
            item.system?.battery !== undefined && typeof item.system.battery === 'number'
          ).length;
          logger.log(`🔋 Found ${count} battery devices from QueryStatus API`);
          setBatteryDeviceCount(count);
        } else {
          // API returned unexpected data structure, fallback to cache
          logger.warn('🔋 QueryStatus API returned unexpected data structure, falling back to cache');
          const batteryDevices = getAllImportedDevicesWithBattery();
          setBatteryDeviceCount(batteryDevices.length);
        }
      } catch (error) {
        logger.error('Failed to load battery device count:', error);
        // Fallback to cache
        const batteryDevices = getAllImportedDevicesWithBattery();
        setBatteryDeviceCount(batteryDevices.length);
      }
    };
    loadBatteryDeviceCount();
  }, [mode]);

  // Track if initial config load has been applied to form while on the notification tab
  // This prevents async config loads from overwriting user's in-progress edits
  // Note: Tab re-entry restoration is handled by isEnteringNotificationTab, not this flag
  const initialConfigAppliedRef = useRef<boolean>(false);
  
  // Track if user has started editing battery alert settings
  // This prevents async loads from overwriting user's in-progress edits
  const userHasEditedBatteryAlertRef = useRef<boolean>(false);
  
  // Restore battery alert settings to saved state when ENTERING the notification tab
  // Also handle the case where config loads AFTER user is already on the tab
  // This prevents overwriting unsaved user changes during config refresh
  useEffect(() => {
    const wasOnNotificationTab = prevActiveSectionRef.current === 'notification';
    const isEnteringNotificationTab = activeSection === 'notification' && !wasOnNotificationTab;
    const isLeavingNotificationTab = prevActiveSectionRef.current === 'notification' && activeSection !== 'notification';
    const isOnNotificationTab = activeSection === 'notification';
    
    // Debug log to trace restoration effect
    if (isOnNotificationTab || isEnteringNotificationTab || isLeavingNotificationTab) {
      logger.log('🔋 [Settings] Restoration effect running:', {
        activeSection,
        prevSection: prevActiveSectionRef.current,
        isEntering: isEnteringNotificationTab,
        isLeaving: isLeavingNotificationTab,
        hasSavedConfig: !!savedBatteryAlertConfig,
        savedEnabled: savedBatteryAlertConfig?.enabled,
        initialApplied: initialConfigAppliedRef.current,
        userEdited: userHasEditedBatteryAlertRef.current
      });
    }
    
    // Reset flags when leaving the tab
    // This ensures a fresh state when the user returns
    if (isLeavingNotificationTab) {
      initialConfigAppliedRef.current = false;
      userHasEditedBatteryAlertRef.current = false;
    }
    
    // Restore settings if:
    // 1. Entering the tab with existing config (discards unsaved changes - by design), OR
    // 2. Already on tab and config just loaded for the first time (async load completed)
    //    BUT only if user hasn't started editing
    const shouldRestore = savedBatteryAlertConfig && (
      isEnteringNotificationTab || 
      (isOnNotificationTab && !initialConfigAppliedRef.current && !userHasEditedBatteryAlertRef.current)
    );
    
    if (shouldRestore) {
      logger.log('🔋 [Settings] Restoring battery alert settings to form:', {
        enabled: savedBatteryAlertConfig.enabled,
        threshold: savedBatteryAlertConfig.threshold,
        notificationChannels: savedBatteryAlertConfig.notificationChannels
      });
      setBatteryAlertEnabled(savedBatteryAlertConfig.enabled || false);
      setBatteryThreshold(savedBatteryAlertConfig.threshold || 20);
      // Use length check since empty arrays are truthy in JavaScript
      setBatteryNotificationChannels(
        savedBatteryAlertConfig.notificationChannels?.length ? savedBatteryAlertConfig.notificationChannels : ['websocket', 'email']
      );
      setUseCustomEmail(savedBatteryAlertConfig.useCustomEmail || false);
      setCustomEmail(savedBatteryAlertConfig.customEmail || '');
      
      // Also sync to System Notification Settings custom email state
      if (savedBatteryAlertConfig.useCustomEmail) {
        setUseNotificationCustomEmail(prev => ({ ...prev, battery: true }));
        setSavedUseNotificationCustomEmail(prev => ({ ...prev, battery: true }));
      }
      if (savedBatteryAlertConfig.customEmail) {
        setNotificationCustomEmails(prev => ({ ...prev, battery: savedBatteryAlertConfig.customEmail }));
        setSavedNotificationCustomEmails(prev => ({ ...prev, battery: savedBatteryAlertConfig.customEmail }));
      }
      
      initialConfigAppliedRef.current = true;
    }
    
    // Update the ref after processing
    prevActiveSectionRef.current = activeSection;
  }, [activeSection, savedBatteryAlertConfig]);

  // Helper function to update favicon in document
  const updateFaviconInDocument = (faviconDataUrl: string) => {
    // Remove existing favicon links
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    existingLinks.forEach(link => link.remove());

    // Create new favicon link
    const link = document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'shortcut icon';
    link.href = faviconDataUrl;
    document.getElementsByTagName('head')[0].appendChild(link);

    // Also set for different favicon types
    const link2 = document.createElement('link');
    link2.type = 'image/x-icon';
    link2.rel = 'icon';
    link2.href = faviconDataUrl;
    document.getElementsByTagName('head')[0].appendChild(link2);
  };

  // Show toast notification
  const showToastNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);

    // Auto-hide after 3 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  // Load devices function - 完全依賴 Ultron Turing 快取
  const loadGroupDevices = useCallback(async () => {
    // First save credentials to localStorage if they exist
    if (credentials.apiKey && credentials.appId && selectedGroupId) {
      const updatedCredentials = { ...credentials, groupId: selectedGroupId };
      localStorage.setItem('ultron-energy-credentials', JSON.stringify(updatedCredentials));
      logger.log('🔧 Settings: Saved credentials before loading devices');
    } else {
      logger.log('🔧 Settings: Missing credentials or group ID, cannot load devices');
      showToastNotification('Please save your API key first', 'error');
      return;
    }

    setIsLoadingDevices(true);
    try {
      // Always use Ultron Turing API - trust Turing's caching mechanism
      logger.log('🔧 Settings: Fetching devices from Ultron Turing API (no local cache)');
      const { powerMeters } = await ultronTuringApi.getPowerMeters();
      logger.log('🔧 Settings: Got', powerMeters.length, 'devices from Ultron Turing');
      logger.log('🔧 Settings: Device IDs:', powerMeters.map(d => d.deviceId));
      setGroupDevices(powerMeters);
      
      // 不使用 localStorage 快取設備列表 - 完全信任 Ultron Turing 的快取機制
      // This prevents double-caching issues and ensures data consistency
      
      // Initialize or clean up selected devices (only cache selections, not device data)
      const storageKey = getSelectedDevicesKey();
      const storedSelectedDevices = localStorage.getItem(storageKey);
      const filteredDevices = filterDevicesByMode(powerMeters);
      
      if (!storedSelectedDevices) {
        // Default: select all relevant devices based on mode
        const newSelection: SelectedDevices = {};
        filteredDevices.forEach(device => {
          newSelection[device.deviceId] = true;
        });
        setSelectedDevices(newSelection);
      } else {
        // Clean up selected devices - remove any that are no longer in the filtered list
        try {
          const currentSelection = JSON.parse(storedSelectedDevices);
          const cleanedSelection: SelectedDevices = {};
          
          // Only keep selections for devices that exist in the current filtered list
          filteredDevices.forEach(device => {
            if (currentSelection[device.deviceId] === true) {
              cleanedSelection[device.deviceId] = true;
            }
          });
          
          // Update if cleaned selection is different
          if (Object.keys(cleanedSelection).length !== Object.keys(currentSelection).length) {
            setSelectedDevices(cleanedSelection);
            localStorage.setItem(storageKey, JSON.stringify(cleanedSelection));
            logger.log(`Cleaned up selected devices: ${Object.keys(currentSelection).length} -> ${Object.keys(cleanedSelection).length}`);
          }
        } catch (e) {
          logger.error('Error cleaning up selected devices:', e);
        }
      }
    } catch (error) {
      logger.error('Error loading group devices:', error);
      setGroupDevices([]);
    } finally {
      setIsLoadingDevices(false);
    }
  }, [selectedGroupId, credentials.apiKey, credentials.appId, credentials.groupId, mode, showToastNotification]);
  
  // Load devices only when group changes (not on every credential change)
  useEffect(() => {
    // Only load if we have valid credentials
    if (credentials.apiKey && credentials.appId && selectedGroupId) {
      loadGroupDevices(); // Always fetch from Ultron Turing API
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId]); // Only depend on selectedGroupId

  // Load notification preferences when notification section is active
  useEffect(() => {
    if (activeSection === 'notification' && !savedNotificationPreferences) {
      loadNotificationPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  // Load notification preferences from server
  const loadNotificationPreferences = async () => {
    setIsLoadingNotificationPrefs(true);
    try {
      const result = await ultronTuringApi.getNotificationPreferences();
      if (result.success && result.data?.preferences) {
        const prefs = result.data.preferences;
        setNotificationPreferences(prefs);
        setSavedNotificationPreferences(prefs);
        logger.log('🔔 Notification preferences loaded:', prefs);
      }
      
      // Load custom email settings from localStorage
      const savedCustomEmailsStr = localStorage.getItem('notification-custom-emails');
      if (savedCustomEmailsStr) {
        try {
          const customEmailSettings = JSON.parse(savedCustomEmailsStr);
          if (customEmailSettings.useCustomEmail) {
            setUseNotificationCustomEmail(customEmailSettings.useCustomEmail);
            setSavedUseNotificationCustomEmail(customEmailSettings.useCustomEmail);
          }
          if (customEmailSettings.customEmails) {
            setNotificationCustomEmails(customEmailSettings.customEmails);
            setSavedNotificationCustomEmails(customEmailSettings.customEmails);
          }
          logger.log('🔔 Custom email settings loaded:', customEmailSettings);
        } catch (e) {
          logger.error('Failed to parse custom email settings:', e);
        }
      }
    } catch (error) {
      logger.error('Failed to load notification preferences:', error);
    } finally {
      setIsLoadingNotificationPrefs(false);
    }
  };

  // Check if notification preferences have changed
  const hasNotificationPreferencesChanges = (): boolean => {
    if (!savedNotificationPreferences) {
      // If no saved state, check if any non-default values
      return Object.keys(useNotificationCustomEmail).some(k => useNotificationCustomEmail[k as keyof typeof useNotificationCustomEmail]) ||
             Object.values(notificationCustomEmails).some(v => v && v.trim() !== '');
    }
    
    const alertChanged = JSON.stringify(notificationPreferences.alert?.sort()) !== 
                         JSON.stringify(savedNotificationPreferences.alert?.sort());
    const automationChanged = JSON.stringify(notificationPreferences.automation?.sort()) !== 
                              JSON.stringify(savedNotificationPreferences.automation?.sort());
    const batteryChanged = JSON.stringify(notificationPreferences.battery?.sort()) !== 
                           JSON.stringify(savedNotificationPreferences.battery?.sort());
    
    // Check custom email toggle changes
    const useCustomEmailChanged = 
      useNotificationCustomEmail.alert !== savedUseNotificationCustomEmail.alert ||
      useNotificationCustomEmail.automation !== savedUseNotificationCustomEmail.automation ||
      useNotificationCustomEmail.battery !== savedUseNotificationCustomEmail.battery;
    
    // Check custom email value changes
    const customEmailValueChanged = 
      notificationCustomEmails.alert !== savedNotificationCustomEmails.alert ||
      notificationCustomEmails.automation !== savedNotificationCustomEmails.automation ||
      notificationCustomEmails.battery !== savedNotificationCustomEmails.battery;
    
    return alertChanged || automationChanged || batteryChanged || useCustomEmailChanged || customEmailValueChanged;
  };

  // Save notification preferences to server
  const handleSaveNotificationPreferences = async () => {
    setIsSavingNotificationPrefs(true);
    try {
      const result = await ultronTuringApi.setNotificationPreferences(notificationPreferences);
      if (result.success) {
        setSavedNotificationPreferences({ ...notificationPreferences });
        
        // Save custom email settings to localStorage (API doesn't support custom emails yet)
        const customEmailSettings = {
          useCustomEmail: useNotificationCustomEmail,
          customEmails: notificationCustomEmails
        };
        localStorage.setItem('notification-custom-emails', JSON.stringify(customEmailSettings));
        
        // Update saved state for change detection
        setSavedUseNotificationCustomEmail({ ...useNotificationCustomEmail });
        setSavedNotificationCustomEmails({ ...notificationCustomEmails });
        
        showToastNotification(t.settings.saveSuccess, 'success');
        logger.log('🔔 Notification preferences saved:', notificationPreferences, customEmailSettings);
      } else {
        showToastNotification(result.error || t.settings.saveFailed, 'error');
      }
    } catch (error) {
      logger.error('Failed to save notification preferences:', error);
      showToastNotification(t.settings.saveFailed, 'error');
    } finally {
      setIsSavingNotificationPrefs(false);
    }
  };

  // Test notification email state
  const [isTestingEmail, setIsTestingEmail] = useState<boolean>(false);

  // Test notification email
  const handleTestNotificationEmail = async (type: 'alert' | 'automation' | 'battery') => {
    setIsTestingEmail(true);
    try {
      // Get the email address to use for testing
      let email: string | undefined;
      
      logger.log('📧 handleTestNotificationEmail called for type:', type);
      logger.log('📧 useNotificationCustomEmail:', useNotificationCustomEmail);
      logger.log('📧 notificationCustomEmails:', notificationCustomEmails);
      logger.log('📧 userInfo?.email:', userInfo?.email);
      
      if (useNotificationCustomEmail[type] && notificationCustomEmails[type]) {
        email = notificationCustomEmails[type];
        logger.log('📧 Using custom email:', email);
      } else {
        email = userInfo?.email;
        logger.log('📧 Using user profile email:', email);
      }
      
      if (!email) {
        showToastNotification(t.settings.noEmailConfigured || '請先設定郵件地址', 'error');
        setIsTestingEmail(false);
        return;
      }
      
      logger.log('📧 Calling testNotificationEmail with:', email);
      const result = await ultronTuringApi.testNotificationEmail(email);
      if (result.success) {
        showToastNotification(t.settings.testEmailSuccess || '測試郵件已發送', 'success');
        logger.log('📧 Test email sent successfully to:', email);
      } else {
        showToastNotification(result.error || t.settings.testEmailFailed || '發送測試郵件失敗', 'error');
      }
    } catch (error) {
      logger.error('Failed to send test email:', error);
      showToastNotification(t.settings.testEmailFailed || '發送測試郵件失敗', 'error');
    } finally {
      setIsTestingEmail(false);
    }
  };

  // Toggle notification channel for a type
  const toggleNotificationChannel = (
    type: 'alert' | 'automation' | 'battery',
    channel: 'websocket' | 'email'
  ) => {
    setNotificationPreferences(prev => {
      const currentChannels = prev[type] || [];
      const hasChannel = currentChannels.includes(channel);
      
      let newChannels: ('websocket' | 'email')[];
      if (hasChannel) {
        newChannels = currentChannels.filter(c => c !== channel);
      } else {
        newChannels = [...currentChannels, channel];
      }
      
      return {
        ...prev,
        [type]: newChannels
      };
    });
  };

  // Handle form input changes
  const handleCredentialChange = (field: keyof ApiCredentials, value: string) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    setTestResult(null); // Clear test result when credentials change
  };
  
  // Handle subscription type change
  const handleSubscriptionTypeChange = (type: 'personal' | 'enterprise') => {
    setSubscriptionType(type);
    // App ID is determined by subscription type
    // All users with the same subscription type have the same App ID
    const newAppId = type === 'enterprise' ? 'gP4hKtHG' : 'KLMGeDMy';
    setCredentials(prev => ({ ...prev, appId: newAppId }));
    setTestResult(null); // Clear test result when subscription type changes
  };
  
  // Handle group selection change
  const handleGroupChange = (groupId: string) => {
    setSelectedGroupId(groupId);
    setCredentials(prev => ({ ...prev, groupId: groupId }));
  };

  const handleSettingChange = (field: string, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // NOTE: handleKeepLoggedInChange removed - auto-refresh is now always enabled

  // Save API Key and fetch groups
  const saveApiKey = async () => {
    if (!credentials.apiKey) {
      showToastNotification('Please provide your API Key', 'error');
      return;
    }

    setIsSavingApiKey(true);
    try {
      // For Turing API, we don't actually need to validate API keys
      // since it uses environment variables. Just save the UI state.
      const isConnected = true; // Always connected via Turing server
      
      if (!credentials.groupId) {
        showToastNotification('Please select a group ID.', 'error');
        setIsSavingApiKey(false);
        return;
      }
      
      // Save credentials to localStorage
      localStorage.setItem('ultron-energy-credentials', JSON.stringify(credentials));
      
      // Fetch available groups using API
      try {
        const locations = await ultronTuringApi.queryLocations();
        const locationsData2: any = locations?.data || locations || {};
        const locationsArray = locationsData2?.locations || locationsData2?.groups || (Array.isArray(locationsData2) ? locationsData2 : []);
        const groups = locationsArray.map((loc: any) => ({
          id: loc.id || loc.groupId || '',
          name: loc.name || 'Main Location'
        }));
        
        if (groups && groups.length > 0) {
          setAvailableGroups(groups);
          
          // Auto-select the first group if none selected
          if (!selectedGroupId && groups.length > 0) {
            setSelectedGroupId(groups[0].id);
            setCredentials(prev => ({ ...prev, groupId: groups[0].id }));
          }
          
          setIsApiKeySaved(true);
          showToastNotification(`API Key saved! Found ${groups.length} group(s).`, 'success');
        } else {
          // If no groups from API, allow manual input
          setIsApiKeySaved(true);
          showToastNotification('API Key saved! Please enter your Group ID manually.', 'success');
        }
      } catch (groupError) {
        logger.error('Error fetching groups:', groupError);
        // Still mark as saved but show error
        setIsApiKeySaved(true);
        showToastNotification('API Key saved but failed to fetch groups. You may need to enter Group ID manually.', 'error');
      }
    } catch (error) {
      showToastNotification('Failed to validate API Key. Please try again.', 'error');
      logger.error('Error saving API key:', error);
    } finally {
      setIsSavingApiKey(false);
    }
  };

  // Test API connection
  const testConnection = async () => {
    if (!credentials.apiKey) {
      setTestResult({
        success: false,
        message: 'Please provide your API Key'
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      // For Turing API, just verify we can get devices
      // The API itself doesn't require authentication since it uses environment variables
      const devices = await ultronTuringApi.queryDevices();
      const isConnected = true; // Turing API is always connected via environment
      
      setTestResult({
        success: isConnected,
        message: `Connected to Ultron Turing! Found ${devices.length} devices.`
      });
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Connection test failed'
      });
    } finally {
      setIsTesting(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    setIsSaving(true);

    try {
      // Save API credentials to localStorage
      // Turing API uses environment variables, so we just save for UI state

      // Save other settings to localStorage
      localStorage.setItem('ultron-energy-settings', JSON.stringify(settings));
      
      // Dispatch event to notify other components of settings change
      window.dispatchEvent(new Event('settings-updated'));

      // Sync dashboard settings to Firestore (for cross-device sync)
      const authMode = sessionStorage.getItem('auth_mode');
      if (authMode === 'oauth') {
        try {
          const firestoreSettings: DashboardSettings = {
            powerMeterRefreshInterval: settings.powerMeterRefreshInterval || 30,
            powerChartRefreshInterval: settings.powerChartRefreshInterval || 1800,
            showOfflineDevices: settings.showOfflineDevices !== false,
            autoRefresh: settings.autoRefresh !== false,
            demoMode: settings.demoMode || false,
            timezone: settings.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            keepLoggedIn: settings.keepLoggedIn || false
          };
          await saveDashboardSettings(firestoreSettings);

          // ✅ Update authService cache to prevent circular dependency
          const { authService } = await import('../services/authService');
          authService.updateKeepLoggedInCache(firestoreSettings.keepLoggedIn);

          logger.log('✅ Dashboard settings synced to Firestore');
        } catch (firestoreError) {
          logger.error('Failed to sync settings to Firestore:', firestoreError);
          // Don't fail the entire save if Firestore sync fails
        }
      }

      // Save credentials to localStorage as well (with encryption in production)
      localStorage.setItem('ultron-energy-credentials', JSON.stringify(credentials));

      // Save selected devices with mode-specific key
      const storageKey = getSelectedDevicesKey();
      localStorage.setItem(storageKey, JSON.stringify(selectedDevices));

      // Sync timezone to Turing Server
      if (settings.timezone) {
        try {
          logger.log('⏰ Syncing timezone to Turing Server:', settings.timezone);
          await ultronTuringApi.configureTenant({ timezone: settings.timezone });
          logger.log('✅ Timezone synced successfully');
        } catch (tzError) {
          logger.error('Failed to sync timezone to Turing Server:', tzError);
          // Don't fail the entire save if timezone sync fails
        }
      }

      // Count only valid selected devices (that exist in current filtered list)
      const filteredDevices = filterDevicesByMode(groupDevices);
      const validSelectedCount = filteredDevices.filter(device =>
        selectedDevices[device.deviceId] === true
      ).length;
      showToastNotification(`Settings saved! ${validSelectedCount} devices selected for dashboard.`, 'success');

    } catch (error) {
      showToastNotification('Failed to save settings. Please try again.', 'error');
      logger.error('Error saving settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle logo upload
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset error
    setLogoError('');

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      setLogoError('請上傳 JPG 或 PNG 格式的圖片');
      return;
    }

    // Validate file size (< 2MB after allowing for larger images)
    if (file.size > 2 * 1024 * 1024) {
      setLogoError('Image size cannot exceed 2MB');
      return;
    }

    setIsUploadingLogo(true);

    // Create FileReader to read and process image
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        // Auto-resize image to appropriate height while maintaining aspect ratio
        const targetHeight = 60; // Target height in pixels (suitable for header)
        const aspectRatio = img.width / img.height;
        const targetWidth = targetHeight * aspectRatio;

        // Create canvas to resize image
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          // Use better quality image smoothing
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw resized image
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          // Convert to base64 with optimized quality
          const resizedBase64 = canvas.toDataURL(file.type, 0.9);

          // Save to localStorage
          localStorage.setItem('ultron-company-logo', resizedBase64);
          setLogoUrl(resizedBase64);
          setIsUploadingLogo(false);

          // Sync to Firestore (for cross-device sync)
          const authMode = sessionStorage.getItem('auth_mode');
          if (authMode === 'oauth') {
            const currentBranding: CompanyBranding = {
              logo: resizedBase64,
              favicon: localStorage.getItem('ultron-company-favicon') || undefined,
              companyName: localStorage.getItem('ultron-company-name') || undefined
            };
            saveCompanyBranding(currentBranding)
              .then(() => logger.log('✅ Company logo synced to Firestore'))
              .catch((error) => logger.error('Failed to sync logo to Firestore:', error));
          }
          showToastNotification('Logo uploaded and optimized successfully!', 'success');
        } else {
          setLogoError('Failed to process image, please try again');
          setIsUploadingLogo(false);
        }
      };
      img.onerror = () => {
        setLogoError('Failed to load image, please try again');
        setIsUploadingLogo(false);
      };
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      setLogoError('讀取檔案失敗，請重試');
      setIsUploadingLogo(false);
    };
    reader.readAsDataURL(file);
  };

  // Remove logo
  const handleRemoveLogo = () => {
    localStorage.removeItem('ultron-company-logo');
    setLogoUrl('');
    showToastNotification('Logo removed successfully', 'success');
  };

  // Handle favicon upload
  const handleFaviconUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset error
    setFaviconError('');

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/x-icon', 'image/ico'].includes(file.type)) {
      setFaviconError('請上傳 JPG, PNG 或 ICO 格式的圖片');
      return;
    }

    // Validate file size (< 500KB for favicon)
    if (file.size > 500 * 1024) {
      setFaviconError('Favicon size cannot exceed 500KB');
      return;
    }

    setIsUploadingFavicon(true);

    // Create FileReader to read and process image
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        // Auto-resize image to 32x32 for favicon
        const targetSize = 32;

        // Create canvas to resize image
        const canvas = document.createElement('canvas');
        canvas.width = targetSize;
        canvas.height = targetSize;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          // Use better quality image smoothing
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // Draw resized image (center and crop if not square)
          const size = Math.min(img.width, img.height);
          const x = (img.width - size) / 2;
          const y = (img.height - size) / 2;
          ctx.drawImage(img, x, y, size, size, 0, 0, targetSize, targetSize);

          // Convert to base64
          const resizedBase64 = canvas.toDataURL('image/png');

          // Save to localStorage
          localStorage.setItem('ultron-company-favicon', resizedBase64);
          setFaviconUrl(resizedBase64);

          // Sync to Firestore (for cross-device sync)
          const authMode = sessionStorage.getItem('auth_mode');
          if (authMode === 'oauth') {
            const currentBranding: CompanyBranding = {
              logo: localStorage.getItem('ultron-company-logo') || undefined,
              favicon: resizedBase64,
              companyName: localStorage.getItem('ultron-company-name') || undefined
            };
            saveCompanyBranding(currentBranding)
              .then(() => logger.log('✅ Favicon synced to Firestore'))
              .catch((error) => logger.error('Failed to sync favicon to Firestore:', error));
          }

          // Apply to document
          updateFaviconInDocument(resizedBase64);

          setIsUploadingFavicon(false);
          showToastNotification('Favicon uploaded successfully!', 'success');
        } else {
          setFaviconError('Failed to process favicon, please try again');
          setIsUploadingFavicon(false);
        }
      };
      img.onerror = () => {
        setFaviconError('Failed to load image, please try again');
        setIsUploadingFavicon(false);
      };
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      setFaviconError('讀取檔案失敗，請重試');
      setIsUploadingFavicon(false);
    };
    reader.readAsDataURL(file);
  };

  // Remove favicon
  const handleRemoveFavicon = () => {
    localStorage.removeItem('ultron-company-favicon');
    setFaviconUrl('');

    // Reset to default favicon
    const existingLinks = document.querySelectorAll("link[rel*='icon']");
    existingLinks.forEach(link => link.remove());

    showToastNotification('Favicon removed successfully', 'success');
  };

  // Save company name
  const handleSaveCompanyName = async () => {
    localStorage.setItem('ultron-company-name', companyName);
    setDisplayCompanyName(companyName);

    // Sync to Firestore (for cross-device sync)
    const authMode = sessionStorage.getItem('auth_mode');
    if (authMode === 'oauth') {
      try {
        const currentBranding: CompanyBranding = {
          logo: localStorage.getItem('ultron-company-logo') || undefined,
          favicon: localStorage.getItem('ultron-company-favicon') || undefined,
          companyName: companyName
        };
        await saveCompanyBranding(currentBranding);
        logger.log('✅ Company name synced to Firestore');
      } catch (error) {
        logger.error('Failed to sync company name to Firestore:', error);
      }
    }

    showToastNotification('Company name saved successfully', 'success');
  };

  // Handle logout
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      // Clear cached groupId to prevent stale data on next login
      sessionStorage.removeItem('user_group_id');
      sessionStorage.removeItem('force_refresh_groupid');
      logger.log('🧹 Cleared cached groupId on logout');

      // Clear OAuth session
      mcpOAuthService.logout();
      // Navigate to login page
      navigate('/login');
    }
  };

  // Handle clear cache (for debugging/troubleshooting)
  const handleClearCache = () => {
    if (window.confirm('Clear all cached data? This will refresh device information on next load.')) {
      sessionStorage.removeItem('user_group_id');
      sessionStorage.removeItem('force_refresh_groupid');
      logger.log('🧹 Cache cleared successfully');
      showToastNotification('Cache cleared successfully. Please refresh the page.', 'success');
    }
  };

  // Check if battery alert settings have changed from saved state
  // Note: Notification channels are now managed in System Notification Settings section
  const hasBatteryAlertChanges = (): boolean => {
    if (!savedBatteryAlertConfig) {
      // No saved config yet - consider it changed if enabled or if any non-default values
      return batteryAlertEnabled || batteryThreshold !== 20;
    }
    
    // Compare current state with saved state (only enabled and threshold)
    return (
      batteryAlertEnabled !== (savedBatteryAlertConfig.enabled || false) ||
      batteryThreshold !== (savedBatteryAlertConfig.threshold || 20)
    );
  };

  // Save Battery Alert configuration
  const handleSaveBatteryAlert = async () => {
    // Use notification preferences for battery channels (from System Notification Settings)
    const batteryChannels = notificationPreferences.battery || ['websocket', 'email'];
    
    // Use custom email from System Notification Settings if enabled
    const useBatteryCustomEmail = useNotificationCustomEmail.battery || false;
    const batteryCustomEmail = notificationCustomEmails.battery || '';
    
    // Debug logging
    logger.log('🔋 [handleSaveBatteryAlert] Custom email settings:', {
      useBatteryCustomEmail,
      batteryCustomEmail,
      useNotificationCustomEmail,
      notificationCustomEmails,
      userEmail: userInfo?.email
    });
    
    const batteryAlertConfig: BatteryAlertSettings = {
      enabled: batteryAlertEnabled,
      threshold: batteryThreshold,
      notificationChannels: batteryChannels,
      useCustomEmail: useBatteryCustomEmail,
      customEmail: batteryCustomEmail
    };

    try {
      // Sync global battery alert to Turing Server FIRST
      // Only persist to localStorage/Firestore after successful server sync
      // This prevents inconsistent state if server sync fails
      // Use custom email if enabled and has value, otherwise use account email
      // Note: Only pass custom email if it's explicitly set and not empty
      const emailToUse = useBatteryCustomEmail && batteryCustomEmail.trim() 
        ? batteryCustomEmail.trim() 
        : (userInfo?.email || undefined);
      
      logger.log('🔋 [handleSaveBatteryAlert] emailToUse:', emailToUse);
      // Note: Notification channels are now managed via System Notification Settings (QueryNotifications set_preferences)
      const result = await syncBatteryAlerts(batteryAlertEnabled, batteryThreshold, emailToUse);

      if (result.failed === 0) {
        // Server sync succeeded - now persist to local storage systems
        localStorage.setItem('ultron-battery-alert', JSON.stringify(batteryAlertConfig));
        
        // Sync to Firestore (for cross-device sync)
        const authMode = sessionStorage.getItem('auth_mode');
        if (authMode === 'oauth') {
          try {
            await saveBatteryAlert(batteryAlertConfig);
            logger.log('✅ Battery alert settings synced to Firestore');
          } catch (error) {
            logger.error('Failed to sync battery alert to Firestore:', error);
            // Continue - localStorage is already updated
          }
        }
        
        // Update saved config state after successful save
        // Also record save timestamp to prevent stale async loads from overwriting
        lastSaveTimeRef.current = Date.now();
        setSavedBatteryAlertConfig(batteryAlertConfig);
        // Reset edit flag since we just saved - allow future async loads to apply
        userHasEditedBatteryAlertRef.current = false;
        showToastNotification(
          batteryAlertEnabled
            ? `已啟用全域電池警報 (伺服器會自動監控所有電池設備)`
            : `已停用電池警報`,
          'success'
        );
        // Only notify App.tsx on successful save
        window.dispatchEvent(new CustomEvent('battery-alert-config-changed'));
      } else {
        showToastNotification(
          `電池警報設定失敗: ${result.errors.join(', ')}`,
          'error'
        );
      }
    } catch (error) {
      logger.error('Failed to sync battery alerts:', error);
      showToastNotification('Failed to sync alerts to Turing Server', 'error');
    }
  };

  // Test Battery Alert
  const handleTestBatteryAlert = async () => {
    setIsTestingBatteryAlert(true);

    try {
      // Trigger real battery check from App.tsx
      logger.log('🧪 Triggering manual battery check...');
      window.dispatchEvent(new Event('test-battery-alert'));

      // Show toast notification
      showToastNotification('Running battery check on all devices...', 'success');

      // Auto-hide after 2 seconds
      setTimeout(() => {
        setIsTestingBatteryAlert(false);
      }, 2000);
    } catch (error) {
      logger.error('Failed to test battery alert:', error);
      showToastNotification('Failed to trigger battery check', 'error');
      setIsTestingBatteryAlert(false);
    }
  };

  // Load battery devices with current battery levels directly from QueryStatus API
  const handleShowBatteryDevices = async () => {
    try {
      // ✅ 直接呼叫 QueryStatus API，不帶任何 device filter，取得所有設備
      const rawStatusData = await ultronTuringApi.queryStatus([], { detailed: true });
      
      logger.log('🔋 [Battery] QueryStatus raw result:', rawStatusData);
      
      if (!rawStatusData?.data || !Array.isArray(rawStatusData.data)) {
        showToastNotification('Failed to get device status from API', 'error');
        return;
      }
      
      // ✅ 直接從 QueryStatus 回應中找有 system.battery 的設備
      const batteryDevices: any[] = [];
      
      rawStatusData.data.forEach((item: any) => {
        const system = item.system;
        if (!system) return;
        
        // ✅ system.battery 就是電池電量！這是判斷電池設備的唯一標準！
        if (system.battery !== undefined && typeof system.battery === 'number') {
          batteryDevices.push({
            sn: system.baseSN,
            deviceName: system.deviceName,
            nickname: item.states?.[0]?.nickname || system.deviceName,
            batteryLevel: system.battery,
            online: system.online,
            lastSeen: system.lastSeen
          });
          logger.log(`🔋 [Battery] Found battery device: ${system.baseSN} = ${system.battery}%`);
        }
      });
      
      logger.log(`🔋 [Battery] Total battery devices found: ${batteryDevices.length}`);
      
      if (batteryDevices.length === 0) {
        showToastNotification('No battery devices found from QueryStatus API', 'error');
        return;
      }

      // Map devices with their current battery levels
      // ✅ 簡化：battery level 已經從 system.battery 直接取得
      const devicesWithLevel = batteryDevices.map((device: any) => {
        const displayName = device.nickname || device.deviceName || device.sn || 'Unknown Device';

        return {
          sn: device.sn,
          name: displayName,
          batteryLevel: device.batteryLevel,
          online: device.online
        };
      });

      // Sort devices by battery level: lowest to highest (undefined/N/A at the end)
      const sortedDevices = devicesWithLevel.sort((a, b) => {
        // If both have battery levels, sort by level (lowest first)
        if (a.batteryLevel !== undefined && b.batteryLevel !== undefined) {
          return a.batteryLevel - b.batteryLevel;
        }
        // If only a has battery level, put a first
        if (a.batteryLevel !== undefined) {
          return -1;
        }
        // If only b has battery level, put b first
        if (b.batteryLevel !== undefined) {
          return 1;
        }
        // Both undefined, maintain original order
        return 0;
      });

      logger.log(`🔋 Loaded ${sortedDevices.length} battery devices from Turing Server (sorted by battery level)`);
      setBatteryDevicesWithLevel(sortedDevices);
      setShowBatteryDevicesModal(true);
    } catch (error) {
      logger.error('Failed to load battery devices:', error);
      showToastNotification('Failed to load battery device list from Turing Server', 'error');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-slate-800/30 bg-[#080c14]">
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className={`flex items-center px-4 py-3 rounded-lg shadow-lg ${
            toastType === 'success' 
              ? 'bg-green-500 text-white' 
              : 'bg-red-500 text-white'
          }`}>
            {toastType === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
            )}
            <span className="font-medium">{toastMessage}</span>
            <button
              onClick={() => setShowToast(false)}
              className="ml-4 hover:opacity-80 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="bg-[#0C2340] shadow-sm border-b border-slate-700/30 flex-shrink-0">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={onBack}
              className="mr-4 p-2 text-slate-400 hover:text-slate-300 text-slate-400 hover:text-slate-200 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-white">
              Settings
            </h1>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-hidden flex">
        {/* Settings Sidebar */}
        <aside className="w-64 bg-[#0C2340] border-r border-slate-700/30 overflow-y-auto flex-shrink-0">
          <nav className="p-4 space-y-2">
            <button
              onClick={() => setActiveSection('company')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center space-x-3 text-base ${
                activeSection === 'company'
                  ? 'bg-blue-900/40 text-white font-bold border border-blue-500/30'
                  : 'text-slate-300 hover:bg-slate-700/30 hover:bg-slate-700/50'
              }`}
            >
              <Image className="w-4 h-4" />
              <span>{t.settings.company}</span>
            </button>

            <button
              onClick={() => setActiveSection('dashboard')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center space-x-3 text-base ${
                activeSection === 'dashboard'
                  ? 'bg-blue-900/40 text-white font-bold border border-blue-500/30'
                  : 'text-slate-300 hover:bg-slate-700/30 hover:bg-slate-700/50'
              }`}
            >
              <Activity className="w-4 h-4" />
              <span>{t.settings.dashboard}</span>
            </button>

            <button
              onClick={() => setActiveSection('notification')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center space-x-3 text-base ${
                activeSection === 'notification'
                  ? 'bg-blue-900/40 text-white font-bold border border-blue-500/30'
                  : 'text-slate-300 hover:bg-slate-700/30 hover:bg-slate-700/50'
              }`}
            >
              <AlertCircle className="w-4 h-4" />
              <span>{t.settings.notification}</span>
            </button>

            <button
              onClick={() => setActiveSection('cache')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center space-x-3 text-base ${
                activeSection === 'cache'
                  ? 'bg-blue-900/40 text-white font-bold border border-blue-500/30'
                  : 'text-slate-300 hover:bg-slate-700/30 hover:bg-slate-700/50'
              }`}
            >
              <RefreshCcw className="w-4 h-4" />
              <span>{t.settings.cache}</span>
            </button>

            <button
              onClick={() => setActiveSection('language')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center space-x-3 text-base ${
                activeSection === 'language'
                  ? 'bg-blue-900/40 text-white font-bold border border-blue-500/30'
                  : 'text-slate-300 hover:bg-slate-700/30 hover:bg-slate-700/50'
              }`}
            >
              <Languages className="w-4 h-4" />
              <span>{t.settings.language}</span>
            </button>

            <button
              onClick={() => setActiveSection('themes')}
              className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center space-x-3 text-base ${
                activeSection === 'themes'
                  ? 'bg-blue-900/40 text-white font-bold border border-blue-500/30'
                  : 'text-slate-300 hover:bg-slate-700/30 hover:bg-slate-700/50'
              }`}
            >
              <Palette className="w-4 h-4" />
              <span>{t.settings.themes || 'Themes'}</span>
            </button>
          </nav>
        </aside>

        {/* Settings Content */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          {/* Company Information Section */}
          {activeSection === 'company' && (
            <div className="space-y-6">
          {/* Company Logo Upload */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Image className="w-5 h-5 mr-2 text-blue-600" />
              {t.settings.companyInfo}
            </h2>
            <div className="space-y-6">
              {/* Company Name */}
              <div>
                <label className="block text-base font-medium text-slate-300 mb-2">
                  {t.settings.companyName}
                </label>
                <div className="flex items-center space-x-2 max-w-md">
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="flex-1 px-3 py-2 border border-slate-700/50 rounded-md focus:ring-2 focus:ring-blue-500 bg-slate-800/50 text-white"
                    placeholder={t.settings.companyName}
                  />
                  <button
                    onClick={handleSaveCompanyName}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors flex items-center space-x-2 flex-shrink-0"
                  >
                    <Save className="w-4 h-4" />
                    <span>{t.common.save}</span>
                  </button>
                </div>
              </div>

              {/* Company Logo */}
              <div className="space-y-4">
                <label className="block text-base font-medium text-slate-300 mb-2">
                  {t.settings.logo}
                </label>
                {logoUrl ? (
                  <div
                    className="relative w-24 h-24 rounded-lg border-2 border-slate-700/30 overflow-hidden group cursor-pointer"
                    onMouseEnter={() => setIsLogoHovered(true)}
                    onMouseLeave={() => setIsLogoHovered(false)}
                  >
                    <img 
                      src={logoUrl} 
                      alt="Company Logo" 
                      className="w-full h-full object-contain"
                    />
                    {isLogoHovered && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center space-x-3 transition-opacity">
                        <button
                          onClick={() => setPreviewImage(logoUrl)}
                          className="p-2 bg-slate-800/50 bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors"
                          title="Preview"
                        >
                          <Eye className="w-5 h-5 text-white" />
                        </button>
                        <button
                          onClick={handleRemoveLogo}
                          className="p-2 bg-slate-800/50 bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5 text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleLogoUpload}
                      disabled={isUploadingLogo}
                      className="hidden"
                    />
                    <div className="flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed border-slate-700/50 rounded-lg hover:border-slate-500 hover:border-slate-500 transition-colors">
                      <Upload className="w-6 h-6 text-slate-400 mb-1" />
                      <span className="text-sm text-slate-400">{t.settings.chooseFile}</span>
                    </div>
                  </label>
                )}
                <p className="text-sm text-slate-400">
                  {t.settings.logoFileFormats}
                </p>
                {logoError && (
                  <p className="text-base text-red-400 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {logoError}
                  </p>
                )}
              </div>

              {/* Favicon */}
              <div className="space-y-4 pt-6 border-t border-slate-700/30">
                <label className="block text-base font-medium text-slate-300 mb-2">
                  {t.settings.faviconLabel}
                </label>
                {faviconUrl ? (
                  <div
                    className="relative w-16 h-16 rounded border-2 border-slate-700/30 overflow-hidden group cursor-pointer"
                    onMouseEnter={() => setIsFaviconHovered(true)}
                    onMouseLeave={() => setIsFaviconHovered(false)}
                  >
                    <img
                      src={faviconUrl}
                      alt="Favicon"
                      className="w-full h-full object-contain"
                    />
                    {isFaviconHovered && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center space-x-2 transition-opacity">
                        <button
                          onClick={() => setPreviewImage(faviconUrl)}
                          className="p-1.5 bg-slate-800/50 bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4 text-white" />
                        </button>
                        <button
                          onClick={handleRemoveFavicon}
                          className="p-1.5 bg-slate-800/50 bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-white" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/x-icon,image/ico"
                      onChange={handleFaviconUpload}
                      disabled={isUploadingFavicon}
                      className="hidden"
                    />
                    <div className="flex flex-col items-center justify-center w-16 h-16 border-2 border-dashed border-slate-700/50 rounded hover:border-slate-500 hover:border-slate-500 transition-colors">
                      <Upload className="w-4 h-4 text-slate-400 mb-1" />
                      <span className="text-sm text-slate-400">{t.settings.chooseFile}</span>
                    </div>
                  </label>
                )}
                <p className="text-sm text-slate-400">
                  {t.settings.faviconFileFormats}
                </p>
                {faviconError && (
                  <p className="text-base text-red-400 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {faviconError}
                  </p>
                )}
              </div>
            </div>
          </div>
            </div>
          )}

          {/* Dashboard Settings Section */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
          {/* Dashboard Settings */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              {t.settings.dashboardSettings}
            </h2>

            {/* Environment Info (for OAuth users) */}
            {isOAuthUser && (() => {
              const session = mcpOAuthService.getSession();
              const environment = session?.environment || 'production';
              return (
                <div className="bg-slate-800/30 border border-slate-700/30 rounded-lg p-4 max-w-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Activity className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-base font-medium text-slate-300">
                          {language === 'zh-TW' ? '連接環境' : 'Connection Environment'}
                        </p>
                        <p className="text-sm text-slate-400 mt-1">
                          {language === 'zh-TW' 
                            ? `目前連接到 ${environment === 'production' ? '正式環境' : '測試環境'}`
                            : `Currently connected to ${environment === 'production' ? 'Production' : 'Staging'} environment`}
                        </p>
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                      environment === 'production'
                        ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                        : 'bg-yellow-600/20 text-yellow-400 border border-yellow-500/30'
                    }`}>
                      {environment === 'production' ? 'PROD' : 'STAGING'}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="space-y-6">
              {/* Timezone Selection */}
              <div>
                <label className="block text-base font-medium text-slate-300 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  {t.settings.timezone}
                </label>
                <select
                  value={settings.timezone}
                  onChange={(e) => handleSettingChange('timezone', e.target.value)}
                  className="w-full max-w-md px-3 py-2 border border-slate-700/50 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-slate-800/50 text-white"
                >
                  <optgroup label="Asia">
                    <option value="Asia/Taipei">台北 (UTC+8)</option>
                    <option value="Asia/Shanghai">上海 (UTC+8)</option>
                    <option value="Asia/Hong_Kong">香港 (UTC+8)</option>
                    <option value="Asia/Singapore">新加坡 (UTC+8)</option>
                    <option value="Asia/Tokyo">東京 (UTC+9)</option>
                    <option value="Asia/Seoul">首爾 (UTC+9)</option>
                    <option value="Asia/Bangkok">曼谷 (UTC+7)</option>
                    <option value="Asia/Jakarta">雅加達 (UTC+7)</option>
                    <option value="Asia/Manila">馬尼拉 (UTC+8)</option>
                    <option value="Asia/Kolkata">加爾各答 (UTC+5:30)</option>
                    <option value="Asia/Dubai">杜拜 (UTC+4)</option>
                  </optgroup>
                  <optgroup label="Americas">
                    <option value="America/New_York">紐約 (UTC-5/-4)</option>
                    <option value="America/Chicago">芝加哥 (UTC-6/-5)</option>
                    <option value="America/Denver">丹佛 (UTC-7/-6)</option>
                    <option value="America/Los_Angeles">洛杉磯 (UTC-8/-7)</option>
                    <option value="America/Toronto">多倫多 (UTC-5/-4)</option>
                    <option value="America/Vancouver">溫哥華 (UTC-8/-7)</option>
                    <option value="America/Mexico_City">墨西哥城 (UTC-6/-5)</option>
                    <option value="America/Sao_Paulo">聖保羅 (UTC-3)</option>
                  </optgroup>
                  <optgroup label="Europe">
                    <option value="Europe/London">倫敦 (UTC+0/+1)</option>
                    <option value="Europe/Paris">巴黎 (UTC+1/+2)</option>
                    <option value="Europe/Berlin">柏林 (UTC+1/+2)</option>
                    <option value="Europe/Amsterdam">阿姆斯特丹 (UTC+1/+2)</option>
                    <option value="Europe/Rome">羅馬 (UTC+1/+2)</option>
                    <option value="Europe/Madrid">馬德里 (UTC+1/+2)</option>
                    <option value="Europe/Moscow">莫斯科 (UTC+3)</option>
                    <option value="Europe/Istanbul">伊斯坦堡 (UTC+3)</option>
                  </optgroup>
                  <optgroup label="Pacific">
                    <option value="Pacific/Auckland">奧克蘭 (UTC+12/+13)</option>
                    <option value="Australia/Sydney">雪梨 (UTC+10/+11)</option>
                    <option value="Australia/Melbourne">墨爾本 (UTC+10/+11)</option>
                    <option value="Australia/Brisbane">布里斯本 (UTC+10)</option>
                    <option value="Australia/Perth">珀斯 (UTC+8)</option>
                    <option value="Pacific/Honolulu">檀香山 (UTC-10)</option>
                  </optgroup>
                  <optgroup label="UTC">
                    <option value="UTC">UTC (協調世界時)</option>
                  </optgroup>
                </select>
                <p className="mt-1 text-sm text-slate-400">
                  時區設定用於正確顯示和計算用電時段
                </p>
              </div>

              {/* Dashboard Grid Card Refresh Interval */}
              <div>
                <label className="block text-base font-medium text-slate-300 mb-2">
                  <Zap className="w-4 h-4 inline mr-1" />
                  {t.settings.gridCardRefreshInterval}
                </label>
                <select
                  value={settings.powerMeterRefreshInterval}
                  onChange={(e) => handleSettingChange('powerMeterRefreshInterval', parseInt(e.target.value))}
                  className="w-full max-w-md px-3 py-2 border border-slate-700/50 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-slate-800/50 text-white"
                >
                  <option value={10}>10 {t.settings.seconds}</option>
                  <option value={30}>30 {t.settings.seconds} {t.settings.recommended}</option>
                  <option value={60}>1 {t.settings.minutes}</option>
                  <option value={120}>2 {t.settings.minutes}</option>
                  <option value={300}>5 {t.settings.minutes}</option>
                </select>
              </div>

              {/* Data Chart Refresh Interval */}
              <div>
                <label className="block text-base font-medium text-slate-300 mb-2">
                  <Activity className="w-4 h-4 inline mr-1" />
                  {t.settings.chartRefreshInterval}
                </label>
                <select
                  value={settings.powerChartRefreshInterval}
                  onChange={(e) => handleSettingChange('powerChartRefreshInterval', parseInt(e.target.value))}
                  className="w-full max-w-md px-3 py-2 border border-slate-700/50 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-slate-800/50 text-white"
                >
                  <option value={300}>5 {t.settings.minutes}</option>
                  <option value={600}>10 {t.settings.minutes}</option>
                  <option value={900}>15 {t.settings.minutes}</option>
                  <option value={1800}>30 {t.settings.minutes} {t.settings.recommended}</option>
                  <option value={3600}>1 {t.settings.hour}</option>
                </select>
              </div>

              {/* Keep Logged In Toggle - 已移除，系統現在永遠啟動自動刷新 */}
            </div>
          </div>
            </div>
          )}

          {/* System Notification Section */}
          {activeSection === 'notification' && (
            <div className="space-y-6">
          {/* Battery Alert Configuration */}
          <div className="space-y-6">
            {/* Title row with toggle - right aligned with battery status button below */}
            <div className="max-w-2xl">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold text-white flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2 text-blue-600" />
                  {t.settings.lowBatteryNotification}
                </h2>
                {/* Toggle Switch - right aligned above battery status button */}
                <div className="flex items-center">
                  <span className="mr-3 text-base font-medium text-slate-300 whitespace-nowrap">
                    {batteryAlertEnabled ? t.common.enabled : t.common.disabled}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={batteryAlertEnabled}
                      onChange={(e) => {
                        setBatteryAlertEnabled(e.target.checked);
                        userHasEditedBatteryAlertRef.current = true;
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 peer-focus:ring-blue-800 rounded-full peer bg-slate-800/50 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-slate-800/50 after:border-slate-700/50 after:border after:rounded-full after:h-5 after:w-5 after:transition-all border-slate-700/50 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
              <p className="text-base text-slate-400 mb-4">
                {t.settings.batteryMonitorDescription}
              </p>
            </div>

            {/* Save button when disabled - to sync disabled state to server */}
            {!batteryAlertEnabled && (
              <div className="mt-4">
                {(() => {
                  const hasChanges = hasBatteryAlertChanges();
                  return (
                    <button
                      onClick={handleSaveBatteryAlert}
                      disabled={!hasChanges}
                      title={!hasChanges ? '沒有變更' : ''}
                      className={`flex items-center px-4 py-2 text-white rounded-md transition-colors ${
                        hasChanges
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-slate-500 cursor-not-allowed'
                      }`}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {t.settings.saveConfiguration}
                    </button>
                  );
                })()}
              </div>
            )}

            {/* Collapsible content - only show when enabled */}
            {batteryAlertEnabled && (
              <>
                {/* Battery Device Count Display */}
                <div className="bg-slate-800/30 bg-[#0C2340] border border-slate-700/30 rounded-lg p-4 mb-6 max-w-2xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Activity className="w-5 h-5 text-blue-600" />
                      <span className="text-base font-medium text-slate-300">
                        {t.settings.batteryDevicesFound}
                      </span>
                      <span className="text-lg font-bold text-blue-400">
                        {batteryDeviceCount}
                      </span>
                    </div>
                    <button
                      onClick={handleShowBatteryDevices}
                      className="flex items-center px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-base"
                    >
                      <Activity className="w-4 h-4 mr-1.5" />
                      {t.settings.batteryStatus}
                    </button>
                  </div>
                  <p className="text-sm text-slate-400">
                    {t.settings.batteryDevicesNote}
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Battery Threshold */}
                  <div>
                    <label className="block text-base font-medium text-slate-300 mb-2">
                      {t.settings.batteryThresholdPercent}
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="50"
                      value={batteryThreshold}
                      onChange={(e) => {
                        setBatteryThreshold(Number(e.target.value));
                        userHasEditedBatteryAlertRef.current = true;
                      }}
                      className="w-24 px-3 py-2 border border-slate-700/50 rounded-md focus:ring-2 focus:ring-blue-500 bg-slate-800/50 text-white"
                    />
                    <p className="mt-1 text-sm text-slate-400">
                      {t.settings.batteryThresholdNote}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-3 pt-4 border-t border-slate-700/30">
                    {(() => {
                      const hasChanges = hasBatteryAlertChanges();
                      
                      return (
                        <>
                          <button
                            onClick={handleSaveBatteryAlert}
                            disabled={!hasChanges}
                            title={!hasChanges ? '沒有變更' : ''}
                            className={`flex items-center px-4 py-2 text-white rounded-md transition-colors ${
                              !hasChanges
                                ? 'bg-slate-500 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                          >
                            <Save className="w-4 h-4 mr-2" />
                            {t.settings.saveConfiguration}
                          </button>

                          <button
                            onClick={handleTestBatteryAlert}
                            disabled={isTestingBatteryAlert}
                            title="測試現有伺服器上的電池警報設定"
                            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors"
                          >
                            {isTestingBatteryAlert ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <TestTube className="w-4 h-4 mr-2" />
                            )}
                            {t.settings.testAlert}
                          </button>
                        </>
                      );
                    })()}
                  </div>

                  {/* Info Message */}
                  <div className="bg-blue-900/20 border border-blue-200 border-blue-800 rounded-lg p-4 max-w-2xl">
                    <p className="text-base text-blue-800 text-blue-300">
                      <strong>Note:</strong> {t.settings.alertCenterNote}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-slate-700/30 my-8"></div>

          {/* System Notification Settings Section */}
          <div className="space-y-6">
            <div className="max-w-2xl">
              <h2 className="text-lg font-semibold text-white flex items-center mb-2">
                <AlertCircle className="w-5 h-5 mr-2 text-purple-600" />
                {t.settings.notificationSettings || '系統通知設定'}
              </h2>
              <p className="text-base text-slate-400 mb-6">
                {t.settings.notificationSettingsDescription || '設定不同類型通知的接收方式'}
              </p>
            </div>

            {isLoadingNotificationPrefs ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-2 text-slate-400">Loading...</span>
              </div>
            ) : (
              <div className="space-y-4 max-w-2xl">
                {/* Alert Notification */}
                <div className="bg-slate-800/30 bg-[#0C2340] border border-slate-700/30 rounded-lg p-4">
                  <h3 className="text-base font-medium text-white mb-3">
                    {t.settings.alertNotification || '警報通知'} (Alert)
                  </h3>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationPreferences.alert?.includes('websocket') || false}
                        onChange={() => toggleNotificationChannel('alert', 'websocket')}
                        className="w-4 h-4 text-blue-600 bg-slate-700/30 border-slate-700/50 rounded focus:ring-blue-500 focus:ring-blue-600 ring-offset-slate-800 focus:ring-2 bg-slate-800/50 border-slate-700/50"
                      />
                      <span className="ml-2 text-base text-slate-300">
                        {t.settings.websocketNotification || '網頁通知'}
                      </span>
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationPreferences.alert?.includes('email') || false}
                          onChange={() => toggleNotificationChannel('alert', 'email')}
                          className="w-4 h-4 text-blue-600 bg-slate-700/30 border-slate-700/50 rounded focus:ring-blue-500 focus:ring-blue-600 ring-offset-slate-800 focus:ring-2 bg-slate-800/50 border-slate-700/50"
                        />
                        <span className="ml-2 text-base text-slate-300">
                          {t.settings.emailNotification || '電子郵件'}
                        </span>
                        {notificationPreferences.alert?.includes('email') && (
                          <span className="ml-2 text-sm text-slate-400">
                            ({useNotificationCustomEmail.alert ? notificationCustomEmails.alert || '請輸入郵件地址' : userInfo?.email || '帳號郵件'})
                          </span>
                        )}
                      </label>
                      {/* Custom email option for Alert */}
                      {notificationPreferences.alert?.includes('email') && (
                        <div className="ml-6 space-y-2">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useNotificationCustomEmail.alert || false}
                              onChange={(e) => setUseNotificationCustomEmail(prev => ({ ...prev, alert: e.target.checked }))}
                              className="w-4 h-4 text-blue-600 bg-slate-700/30 border-slate-700/50 rounded focus:ring-blue-500 focus:ring-blue-600 ring-offset-slate-800 focus:ring-2 bg-slate-800/50 border-slate-700/50"
                            />
                            <span className="ml-2 text-base text-slate-300">
                              {t.settings.useCustomEmailAddress || '使用其他郵件地址'}
                            </span>
                          </label>
                          {useNotificationCustomEmail.alert ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="email"
                                value={notificationCustomEmails.alert || ''}
                                onChange={(e) => setNotificationCustomEmails(prev => ({ ...prev, alert: e.target.value }))}
                                placeholder={userInfo?.email || '請輸入郵件地址'}
                                className="flex-1 max-w-xs px-3 py-1.5 text-base border border-slate-700/50 rounded-md focus:ring-2 focus:ring-blue-500 bg-slate-800/50 text-white"
                              />
                              <button
                                onClick={() => handleTestNotificationEmail('alert')}
                                disabled={isTestingEmail}
                                className={`flex items-center px-3 py-1.5 text-base border rounded-md transition-colors whitespace-nowrap ${
                                  isTestingEmail
                                    ? 'bg-slate-700/30 border-slate-700/50 text-slate-400 cursor-not-allowed bg-slate-800/50 border-slate-700/50 text-slate-400'
                                    : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:bg-slate-800/30 bg-[#0C2340] border-slate-700/50 text-slate-300 hover:bg-slate-700/50'
                                }`}
                              >
                                {isTestingEmail ? (
                                  <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4 mr-1.5" />
                                )}
                                {t.settings.testEmail || '發送測試郵件'}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleTestNotificationEmail('alert')}
                              disabled={isTestingEmail}
                              className={`flex items-center px-3 py-1.5 text-base border rounded-md transition-colors whitespace-nowrap ${
                                isTestingEmail
                                  ? 'bg-slate-700/30 border-slate-700/50 text-slate-400 cursor-not-allowed bg-slate-800/50 border-slate-700/50 text-slate-400'
                                  : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:bg-slate-800/30 bg-[#0C2340] border-slate-700/50 text-slate-300 hover:bg-slate-700/50'
                              }`}
                            >
                              {isTestingEmail ? (
                                <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                              ) : (
                                <Mail className="w-4 h-4 mr-1.5" />
                              )}
                              {t.settings.testEmail || '發送測試郵件'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Automation Notification */}
                <div className="bg-slate-800/30 bg-[#0C2340] border border-slate-700/30 rounded-lg p-4">
                  <h3 className="text-base font-medium text-white mb-3">
                    {t.settings.automationNotification || '自動化通知'} (Automation)
                  </h3>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationPreferences.automation?.includes('websocket') || false}
                        onChange={() => toggleNotificationChannel('automation', 'websocket')}
                        className="w-4 h-4 text-blue-600 bg-slate-700/30 border-slate-700/50 rounded focus:ring-blue-500 focus:ring-blue-600 ring-offset-slate-800 focus:ring-2 bg-slate-800/50 border-slate-700/50"
                      />
                      <span className="ml-2 text-base text-slate-300">
                        {t.settings.websocketNotification || '網頁通知'}
                      </span>
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationPreferences.automation?.includes('email') || false}
                          onChange={() => toggleNotificationChannel('automation', 'email')}
                          className="w-4 h-4 text-blue-600 bg-slate-700/30 border-slate-700/50 rounded focus:ring-blue-500 focus:ring-blue-600 ring-offset-slate-800 focus:ring-2 bg-slate-800/50 border-slate-700/50"
                        />
                        <span className="ml-2 text-base text-slate-300">
                          {t.settings.emailNotification || '電子郵件'}
                        </span>
                        {notificationPreferences.automation?.includes('email') && (
                          <span className="ml-2 text-sm text-slate-400">
                            ({useNotificationCustomEmail.automation ? notificationCustomEmails.automation || '請輸入郵件地址' : userInfo?.email || '帳號郵件'})
                          </span>
                        )}
                      </label>
                      {/* Custom email option for Automation */}
                      {notificationPreferences.automation?.includes('email') && (
                        <div className="ml-6 space-y-2">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useNotificationCustomEmail.automation || false}
                              onChange={(e) => setUseNotificationCustomEmail(prev => ({ ...prev, automation: e.target.checked }))}
                              className="w-4 h-4 text-blue-600 bg-slate-700/30 border-slate-700/50 rounded focus:ring-blue-500 focus:ring-blue-600 ring-offset-slate-800 focus:ring-2 bg-slate-800/50 border-slate-700/50"
                            />
                            <span className="ml-2 text-base text-slate-300">
                              {t.settings.useCustomEmailAddress || '使用其他郵件地址'}
                            </span>
                          </label>
                          {useNotificationCustomEmail.automation ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="email"
                                value={notificationCustomEmails.automation || ''}
                                onChange={(e) => setNotificationCustomEmails(prev => ({ ...prev, automation: e.target.value }))}
                                placeholder={userInfo?.email || '請輸入郵件地址'}
                                className="flex-1 max-w-xs px-3 py-1.5 text-base border border-slate-700/50 rounded-md focus:ring-2 focus:ring-blue-500 bg-slate-800/50 text-white"
                              />
                              <button
                                onClick={() => handleTestNotificationEmail('automation')}
                                disabled={isTestingEmail}
                                className={`flex items-center px-3 py-1.5 text-base border rounded-md transition-colors whitespace-nowrap ${
                                  isTestingEmail
                                    ? 'bg-slate-700/30 border-slate-700/50 text-slate-400 cursor-not-allowed bg-slate-800/50 border-slate-700/50 text-slate-400'
                                    : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:bg-slate-800/30 bg-[#0C2340] border-slate-700/50 text-slate-300 hover:bg-slate-700/50'
                                }`}
                              >
                                {isTestingEmail ? (
                                  <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4 mr-1.5" />
                                )}
                                {t.settings.testEmail || '發送測試郵件'}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleTestNotificationEmail('automation')}
                              disabled={isTestingEmail}
                              className={`flex items-center px-3 py-1.5 text-base border rounded-md transition-colors whitespace-nowrap ${
                                isTestingEmail
                                  ? 'bg-slate-700/30 border-slate-700/50 text-slate-400 cursor-not-allowed bg-slate-800/50 border-slate-700/50 text-slate-400'
                                  : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:bg-slate-800/30 bg-[#0C2340] border-slate-700/50 text-slate-300 hover:bg-slate-700/50'
                              }`}
                            >
                              {isTestingEmail ? (
                                <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                              ) : (
                                <Mail className="w-4 h-4 mr-1.5" />
                              )}
                              {t.settings.testEmail || '發送測試郵件'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Battery Notification */}
                <div className="bg-slate-800/30 bg-[#0C2340] border border-slate-700/30 rounded-lg p-4">
                  <h3 className="text-base font-medium text-white mb-3">
                    {t.settings.batteryNotification || '電池通知'} (Battery)
                  </h3>
                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationPreferences.battery?.includes('websocket') || false}
                        onChange={() => toggleNotificationChannel('battery', 'websocket')}
                        className="w-4 h-4 text-blue-600 bg-slate-700/30 border-slate-700/50 rounded focus:ring-blue-500 focus:ring-blue-600 ring-offset-slate-800 focus:ring-2 bg-slate-800/50 border-slate-700/50"
                      />
                      <span className="ml-2 text-base text-slate-300">
                        {t.settings.websocketNotification || '網頁通知'}
                      </span>
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notificationPreferences.battery?.includes('email') || false}
                          onChange={() => toggleNotificationChannel('battery', 'email')}
                          className="w-4 h-4 text-blue-600 bg-slate-700/30 border-slate-700/50 rounded focus:ring-blue-500 focus:ring-blue-600 ring-offset-slate-800 focus:ring-2 bg-slate-800/50 border-slate-700/50"
                        />
                        <span className="ml-2 text-base text-slate-300">
                          {t.settings.emailNotification || '電子郵件'}
                        </span>
                        {notificationPreferences.battery?.includes('email') && (
                          <span className="ml-2 text-sm text-slate-400">
                            ({useNotificationCustomEmail.battery ? notificationCustomEmails.battery || '請輸入郵件地址' : userInfo?.email || '帳號郵件'})
                          </span>
                        )}
                      </label>
                      {/* Custom email option for Battery */}
                      {notificationPreferences.battery?.includes('email') && (
                        <div className="ml-6 space-y-2">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useNotificationCustomEmail.battery || false}
                              onChange={(e) => setUseNotificationCustomEmail(prev => ({ ...prev, battery: e.target.checked }))}
                              className="w-4 h-4 text-blue-600 bg-slate-700/30 border-slate-700/50 rounded focus:ring-blue-500 focus:ring-blue-600 ring-offset-slate-800 focus:ring-2 bg-slate-800/50 border-slate-700/50"
                            />
                            <span className="ml-2 text-base text-slate-300">
                              {t.settings.useCustomEmailAddress || '使用其他郵件地址'}
                            </span>
                          </label>
                          {useNotificationCustomEmail.battery ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="email"
                                value={notificationCustomEmails.battery || ''}
                                onChange={(e) => setNotificationCustomEmails(prev => ({ ...prev, battery: e.target.value }))}
                                placeholder={userInfo?.email || '請輸入郵件地址'}
                                className="flex-1 max-w-xs px-3 py-1.5 text-base border border-slate-700/50 rounded-md focus:ring-2 focus:ring-blue-500 bg-slate-800/50 text-white"
                              />
                              <button
                                onClick={() => handleTestNotificationEmail('battery')}
                                disabled={isTestingEmail}
                                className={`flex items-center px-3 py-1.5 text-base border rounded-md transition-colors whitespace-nowrap ${
                                  isTestingEmail
                                    ? 'bg-slate-700/30 border-slate-700/50 text-slate-400 cursor-not-allowed bg-slate-800/50 border-slate-700/50 text-slate-400'
                                    : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:bg-slate-800/30 bg-[#0C2340] border-slate-700/50 text-slate-300 hover:bg-slate-700/50'
                                }`}
                              >
                                {isTestingEmail ? (
                                  <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4 mr-1.5" />
                                )}
                                {t.settings.testEmail || '發送測試郵件'}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleTestNotificationEmail('battery')}
                              disabled={isTestingEmail}
                              className={`flex items-center px-3 py-1.5 text-base border rounded-md transition-colors whitespace-nowrap ${
                                isTestingEmail
                                  ? 'bg-slate-700/30 border-slate-700/50 text-slate-400 cursor-not-allowed bg-slate-800/50 border-slate-700/50 text-slate-400'
                                  : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:bg-slate-800/30 bg-[#0C2340] border-slate-700/50 text-slate-300 hover:bg-slate-700/50'
                              }`}
                            >
                              {isTestingEmail ? (
                                <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" />
                              ) : (
                                <Mail className="w-4 h-4 mr-1.5" />
                              )}
                              {t.settings.testEmail || '發送測試郵件'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="pt-4">
                  <button
                    onClick={handleSaveNotificationPreferences}
                    disabled={!hasNotificationPreferencesChanges() || isSavingNotificationPrefs}
                    className={`flex items-center px-4 py-2 text-white rounded-md transition-colors ${
                      !hasNotificationPreferencesChanges() || isSavingNotificationPrefs
                        ? 'bg-slate-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isSavingNotificationPrefs ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {t.settings.saveNotificationSettings || '儲存通知設定'}
                  </button>
                </div>
              </div>
            )}
          </div>
            </div>
          )}

          {/* Cache Management Section */}
          {activeSection === 'cache' && (
            <div className="space-y-6">
          {/* Cache Management */}
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              {t.settings.cacheManagement}
            </h2>
            <p className="text-base text-slate-400 mb-4">
              {t.settings.cacheManagementDescription}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  if (window.confirm(t.settings.confirmClearIncrementalData)) {
                    // Clear incremental data
                    const keys = Object.keys(localStorage);
                    let count = 0;
                    keys.forEach(key => {
                      if (key.startsWith('incremental-data-')) {
                        localStorage.removeItem(key);
                        count++;
                      }
                    });
                    showToastNotification(t.settings.clearedIncrementalCaches.replace('{count}', count.toString()), 'success');
                  }
                }}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
              >
                {t.settings.clearIncrementalData}
              </button>
              <button
                onClick={() => {
                  if (window.confirm(t.settings.confirmClearAllCache)) {
                    // Clear all cache except settings
                    const keys = Object.keys(localStorage);
                    let count = 0;
                    keys.forEach(key => {
                      if (!key.startsWith('ultron-api-') &&
                          !key.startsWith('ultron-selected-') &&
                          !key.startsWith('ultron-energy-')) {
                        localStorage.removeItem(key);
                        count++;
                      }
                    });
                    showToastNotification(t.settings.clearedCacheItems.replace('{count}', count.toString()), 'success');
                  }
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                {t.settings.clearAllCache}
              </button>
            </div>
            <p className="mt-3 text-sm text-slate-400">
              {t.settings.storageUsed} {(() => {
                let totalSize = 0;
                for (let i = 0; i < localStorage.length; i++) {
                  const key = localStorage.key(i);
                  if (key) {
                    const value = localStorage.getItem(key);
                    if (value) {
                      totalSize += key.length + value.length;
                    }
                  }
                }
                return (totalSize / 1024 / 1024).toFixed(2);
              })()} MB of ~5 MB
            </p>
          </div>
            </div>
          )}

          {/* Language Settings Section */}
          {activeSection === 'language' && (
            <div className="space-y-6">
              <div className="space-y-6">
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Languages className="w-5 h-5 mr-2 text-blue-600" />
                  {t.settings.language}
                </h2>

                {/* Language Selection */}
                <div className="max-w-xl">
                  <label className="block text-base font-medium text-slate-300 mb-2">
                    {t.settings.selectLanguage}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Traditional Chinese */}
                    <button
                      onClick={() => setLanguage('zh-TW')}
                      className={`flex items-center justify-center px-6 py-4 rounded-lg border-2 transition-all ${
                        language === 'zh-TW'
                          ? 'border-blue-500 bg-blue-900/20 text-blue-700 text-blue-300'
                          : 'border-slate-700/50 hover:border-slate-500 hover:border-slate-500 text-slate-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">🇹🇼</div>
                        <div className="font-medium">{t.settings.traditionalChinese}</div>
                        {language === 'zh-TW' && (
                          <div className="text-sm mt-1 opacity-75">
                            {t.settings.currentLanguage}
                          </div>
                        )}
                      </div>
                    </button>

                    {/* English */}
                    <button
                      onClick={() => setLanguage('en-US')}
                      className={`flex items-center justify-center px-6 py-4 rounded-lg border-2 transition-all ${
                        language === 'en-US'
                          ? 'border-blue-500 bg-blue-900/20 text-blue-700 text-blue-300'
                          : 'border-slate-700/50 hover:border-slate-500 hover:border-slate-500 text-slate-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">🇺🇸</div>
                        <div className="font-medium">{t.settings.english}</div>
                        {language === 'en-US' && (
                          <div className="text-sm mt-1 opacity-75">
                            {t.settings.currentLanguage}
                          </div>
                        )}
                      </div>
                    </button>
                  </div>
                </div>

                {/* Info Message */}
                <div className="bg-blue-900/20 border border-blue-200 border-blue-800 rounded-lg p-4 max-w-xl">
                  <p className="text-base text-blue-800 text-blue-300">
                    <strong>Note:</strong> Language changes are applied immediately and saved in your browser. The interface will update automatically.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Device Modal Themes Section */}
          {activeSection === 'themes' && (
            <div className="h-full -mx-8 -my-8">
              <DeviceModalThemeEditor />
            </div>
          )}

          {/* Save Button - Show for Dashboard section only */}
          {activeSection === 'dashboard' && (
            <div className="flex items-center justify-start gap-3 mt-8 pt-6 border-t border-slate-700/30">
              <button
                onClick={saveSettings}
                disabled={isSaving}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
              >
                {isSaving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Settings
              </button>
              {isOAuthUser && (
                <button
                  onClick={handleClearCache}
                  className="flex items-center px-6 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 transition-colors"
                  title="Clear cached device data (for troubleshooting)"
                >
                  <RefreshCcw className="w-4 h-4 mr-2" />
                  Clear Cache
                </button>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Battery Devices Modal */}
      {showBatteryDevicesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#0C2340] rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700/30">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <Activity className="w-5 h-5 mr-2 text-purple-600" />
                Battery Devices ({batteryDevicesWithLevel.length})
              </h3>
              <button
                onClick={() => setShowBatteryDevicesModal(false)}
                className="text-slate-400 hover:text-slate-400 hover:text-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-3">
                {batteryDevicesWithLevel.map((device) => (
                  <div
                    key={device.sn}
                    className="flex items-center justify-between p-4 bg-slate-800/30 bg-slate-800/50 rounded-lg border border-slate-700/30 border-slate-700/50"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-white">
                        {device.name}
                      </p>
                      <p className="text-base text-slate-400 mt-1">
                        SN: {device.sn}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div
                        className={`px-3 py-1 rounded-full text-base font-bold text-white ${
                          device.batteryLevel === undefined
                            ? 'bg-slate-600'
                            : device.batteryLevel <= 20
                            ? 'bg-red-600'
                            : device.batteryLevel <= 50
                            ? 'bg-yellow-600'
                            : 'bg-green-600'
                        }`}
                      >
                        {device.batteryLevel !== undefined
                          ? `${device.batteryLevel}%`
                          : 'N/A'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-slate-700/30 flex justify-end">
              <button
                onClick={() => setShowBatteryDevicesModal(false)}
                className="px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 text-white hover:text-slate-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-[#0C2340] border-t border-slate-700/30 flex-shrink-0">
        <div className="w-full px-4 sm:px-6 lg:px-8" style={{ padding: '8px' }}>
          <div className="flex justify-between items-center relative">
            <div className="text-base text-slate-400">
              Version: {APP_VERSION}
            </div>
            <p className="text-base font-medium text-slate-300 absolute left-1/2 transform -translate-x-1/2">
              {displayCompanyName}
            </p>
            <a
              href="https://www.ultronsmart.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-base text-slate-400 hover:cursor-pointer"
            >
              Powered by ULTRON
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Settings;